import * as React from 'react';
import moment from 'moment';
import { connectWithRouter } from 'helpers/connectWithRouter';
import { bindActionCreators, Dispatch } from 'redux';
import { hasPermission } from 'helpers/Permission';
import { getOrgExpenses, getMoreOrgExpenses, emptyOrgExpenses, OrgExpensesParams } from 'actions/Expenses/Get';
import { editExpense } from 'actions/Expenses/Edit';
import RecordsTable, { Column, BulkAction } from './RecordsTable';
import EditRecordModal from './EditRecordModal';
import SyncStatusFilterBar from './SyncStatusFilterBar';
import { ReduxState } from 'reducers/types';
import { PermissionTypes } from '../../constants';
import { QUERY_LIMIT_25 } from '../../configs/General';
import CircularProgress from '@mui/material/CircularProgress';
import { Expense, Cursor, SYNC_STATUS_OPTIONS } from './expenseTypes';

const styles = require('./recordStyles.pcss');

const COLUMNS: Column[] = [
  {
    label: 'Expense',
    render: (r: Expense) => r.title ?? r.merchant ?? '',
  },
  {
    label: 'Date',
    render: (r: Expense) => {
      const raw = r.date ?? r.created ?? '';
      return moment(raw).isValid() ? moment(raw).format('MMM DD, YYYY') : '';
    },
  },
  {
    label: 'Amount',
    render: (r: Expense) => `$${(r.amount ?? 0).toFixed(2)}`,
  },
  {
    label: 'Status',
    render: (r: Expense) => r.status ?? '',
  },
  {
    label: 'Sync Status',
    render: (r: Expense) => r.syncToGlStatus ?? '',
  },
];

type Props = {
  organizationId: string;
  expenses: Expense[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  cursor: Cursor | null;
  success: boolean | null;
  getOrgExpenses: (organizationId: string, params?: OrgExpensesParams) => void;
  getMoreOrgExpenses: (organizationId: string, params: OrgExpensesParams, cursor: Cursor) => void;
  editExpense: (params: { expenseIds: string[]; syncToGlStatus: string }) => void;
  emptyOrgExpenses: () => void;
};

type State = {
  editModalOpen: boolean;
  selectedExpense: Expense | null;
  filterSyncStatus: string;
  filterDateFrom: string;
  filterDateTo: string;
};

class OrgExpensesTable extends React.Component<Props, State> {
  private sentinelRef = React.createRef<HTMLDivElement>();
  private observer: IntersectionObserver | null = null;

  constructor(props: Props) {
    super(props);

    this.state = {
      editModalOpen: false,
      selectedExpense: null,
      filterSyncStatus: '',
      filterDateFrom: '',
      filterDateTo: '',
    };
  }

  componentDidMount() {
    if (this.props.organizationId) {
      this.fetchExpenses();
    }
    this.setupObserver();
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.success !== this.props.success && this.props.success === true) {
      this.fetchExpenses();
      this.setState({ editModalOpen: false, selectedExpense: null });
    }

    // Re-attach observer when hasMore or isLoadingMore changes
    if (prevProps.hasMore !== this.props.hasMore || prevProps.isLoadingMore !== this.props.isLoadingMore) {
      this.setupObserver();
    }
  }

  componentWillUnmount() {
    if (this.observer) {
      this.observer.disconnect();
    }
    this.props.emptyOrgExpenses();
  }

  setupObserver = () => {
    if (this.observer) {
      this.observer.disconnect();
    }

    const sentinel = this.sentinelRef.current;
    if (!sentinel || !this.props.hasMore || this.props.isLoadingMore) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          this.loadMore();
        }
      },
      { threshold: 0.1 },
    );

    this.observer.observe(sentinel);
  };

  getFilterParams = (): OrgExpensesParams => {
    const params: OrgExpensesParams = { limit: QUERY_LIMIT_25 };
    if (this.state.filterSyncStatus) {
      params.syncToGlStatus = this.state.filterSyncStatus;
    }
    if (this.state.filterDateFrom) {
      params.dateFrom = this.state.filterDateFrom;
    }
    if (this.state.filterDateTo) {
      params.dateTo = this.state.filterDateTo;
    }
    return params;
  };

  fetchExpenses = () => {
    this.props.getOrgExpenses(this.props.organizationId, this.getFilterParams());
  };

  loadMore = () => {
    if (this.props.cursor && this.props.hasMore && !this.props.isLoadingMore) {
      this.props.getMoreOrgExpenses(
        this.props.organizationId,
        this.getFilterParams(),
        this.props.cursor,
      );
    }
  };

  handleFilterApply = () => {
    this.fetchExpenses();
  };

  handleFilterReset = () => {
    this.setState(
      { filterSyncStatus: '', filterDateFrom: '', filterDateTo: '' },
      () => this.fetchExpenses(),
    );
  };

  onClickRow = (expense: Expense) => {
    if (hasPermission('expenseReport', PermissionTypes.update)) {
      this.setState({ editModalOpen: true, selectedExpense: expense });
    }
  };

  onCloseModal = () => {
    this.setState({ editModalOpen: false, selectedExpense: null });
  };

  onSubmitEdit = (values: Record<string, string>) => {
    const { selectedExpense } = this.state;
    if (selectedExpense?.id) {
      this.props.editExpense({
        expenseIds: [selectedExpense.id],
        syncToGlStatus: values.syncToGlStatus,
      });
    }
  };

  getDisplayFields = () => {
    const e = this.state.selectedExpense;
    return [
      { label: 'Expense', value: e?.title ?? e?.merchant ?? '' },
      { label: 'Status', value: e?.status ?? '' },
      {
        label: 'Amount',
        value: `$${(e?.amount ?? 0).toFixed(2)}`,
      },
    ];
  };

  getEditFields = () => [
    {
      label: 'Sync to GL Status',
      key: 'syncToGlStatus',
      options: SYNC_STATUS_OPTIONS,
    },
  ];

  getInitialValues = () => ({
    syncToGlStatus: this.state.selectedExpense?.syncToGlStatus ?? '',
  });

  onBulkUpdateSyncStatus = (selectedIds: string[], status: string) => {
    this.props.editExpense({
      expenseIds: selectedIds,
      syncToGlStatus: status,
    });
  };

  getBulkActions = (): BulkAction[] => {
    return SYNC_STATUS_OPTIONS.map((opt) => ({
      label: `Set ${opt.label}`,
      onClick: (ids: string[]) => this.onBulkUpdateSyncStatus(ids, opt.value),
    }));
  };

  render() {
    const canEdit = hasPermission('expenseReport', PermissionTypes.update);

    return (
      <>
        <SyncStatusFilterBar
          labelId="expense-sync-filter-label"
          filterSyncStatus={this.state.filterSyncStatus}
          filterDateFrom={this.state.filterDateFrom}
          filterDateTo={this.state.filterDateTo}
          onSyncStatusChange={(value) => this.setState({ filterSyncStatus: value })}
          onDateFromChange={(value) => this.setState({ filterDateFrom: value })}
          onDateToChange={(value) => this.setState({ filterDateTo: value })}
          onApply={this.handleFilterApply}
          onReset={this.handleFilterReset}
        />

        <RecordsTable
          columns={COLUMNS}
          records={this.props.expenses}
          tabs={[]}
          isLoading={this.props.isLoading}
          onClickRow={canEdit ? this.onClickRow : undefined}
          keyField="id"
          selectable={canEdit}
          bulkActions={canEdit ? this.getBulkActions() : undefined}
        />

        {this.props.hasMore && (
          <div ref={this.sentinelRef} className={styles.sentinelLoader}>
            {this.props.isLoadingMore && <CircularProgress size={24} />}
          </div>
        )}

        <EditRecordModal
          open={this.state.editModalOpen}
          title="Edit Expense"
          displayFields={this.getDisplayFields()}
          editFields={this.getEditFields()}
          initialValues={this.getInitialValues()}
          onClose={this.onCloseModal}
          onSubmit={this.onSubmitEdit}
        />
      </>
    );
  }
}

function mapStateToProps(state: ReduxState) {
  return {
    expenses: state.orgExpenses.expenses,
    isLoading: state.orgExpenses.isLoading,
    isLoadingMore: state.orgExpenses.isLoadingMore,
    hasMore: state.orgExpenses.hasMore,
    cursor: state.orgExpenses.cursor,
    success: state.orgExpenses.success,
  };
}

function mapDispatchToProps(dispatch: Dispatch) {
  return bindActionCreators(
    {
      getOrgExpenses,
      getMoreOrgExpenses,
      editExpense,
      emptyOrgExpenses,
    },
    dispatch,
  );
}

export default connectWithRouter(
  mapStateToProps,
  mapDispatchToProps,
)(OrgExpensesTable);
